import { z } from 'zod';
import {
  AssetKind,
  AssetOrigin,
  Availability,
  ChannelLine,
  DocumentKind,
  FormatScope,
  JobStatus,
  JobType,
  MarketingFamily,
  MemberRole,
  ProjectStage,
  Provenance,
  PublicationStatus,
  RuleFamily,
  RuleSeverity,
  Calibration,
  Timestamp,
  Uuid,
  VideoFormat,
} from './common.ts';

// Formas de respuesta. Son "loose": el servidor puede añadir campos compatibles
// sin romper clientes v1; los campos listados son el contrato mínimo.

export const WorkspaceSummary = z.looseObject({
  id: Uuid,
  name: z.string(),
  role: MemberRole,
  timezone: z.string(),
  status: z.enum(['active', 'closing', 'closed']),
  change_seq: z.number(),
});

export const Quota = z.looseObject({
  remote_bytes_limit: z.number(),
  bytes_reserved: z.number(),
  bytes_used: z.number(),
  records_limit: z.number(),
  records_used: z.number(),
  bytes_alert: z.boolean(),
});

export const Channel = z.looseObject({
  id: Uuid,
  workspace_id: Uuid,
  name: z.string(),
  line: ChannelLine,
  language: z.string(),
  audience: z.string(),
  timezone: z.string(),
  identity: z.record(z.string(), z.unknown()),
  publishing_policy: z.record(z.string(), z.unknown()),
  active_edit_profile_version_id: Uuid.nullable(),
  archived_at: z.string().nullable(),
});
export type Channel = z.infer<typeof Channel>;

export const Rule = z.looseObject({
  id: Uuid,
  rule_key: z.string(),
  family: RuleFamily,
  scope: FormatScope,
  severity: RuleSeverity,
  parameter: z.string(),
  criterion: z.string(),
  value: z.unknown(),
  unit: z.string().nullable(),
  calibration: Calibration,
  example: z.string(),
  counterexample: z.string(),
  reason: z.string(),
  author_kind: z.enum(['human', 'agent', 'process']),
});
export type Rule = z.infer<typeof Rule>;

export const RuleInput = z.object({
  rule_key: z.string().regex(/^[a-z0-9_.-]{2,80}$/),
  family: RuleFamily,
  scope: FormatScope.optional(),
  severity: RuleSeverity,
  parameter: z.string().max(200).optional(),
  criterion: z.string().min(1).max(2000),
  value: z.unknown().optional(),
  unit: z.string().max(40).nullable().optional(),
  calibration: Calibration.optional(),
  example: z.string().max(4000).optional(),
  counterexample: z.string().max(4000).optional(),
  reason: z.string().max(4000).optional(),
});
export type RuleInput = z.infer<typeof RuleInput>;

export const ProfileVersionSummary = z.looseObject({
  id: Uuid,
  version: z.number(),
  status: z.enum(['draft', 'approved', 'superseded']),
  approved_at: z.string().nullable(),
});

export const Profile = z.looseObject({
  id: Uuid,
  channel_id: Uuid,
  kind: z.enum(['edit', 'marketing', 'language']),
  name: z.string(),
  versions: z.array(ProfileVersionSummary),
});

export const Approval = z.looseObject({
  id: Uuid,
  target: z.enum(['script', 'voice', 'edit_plan', 'deliverable']),
  revision_id: Uuid.nullable(),
  deliverable_id: Uuid.nullable().optional(),
  content_hash: z.string(),
  approved_at: z.string(),
});

export const Project = z.looseObject({
  id: Uuid,
  workspace_id: Uuid,
  channel_id: Uuid,
  source_project_id: Uuid.nullable().optional(),
  title: z.string(),
  format: VideoFormat,
  stage: ProjectStage,
  brief: z.record(z.string(), z.unknown()),
  profile_version_id: Uuid.nullable(),
  status: z.enum(['active', 'archived']),
  channel: z.looseObject({ id: Uuid, name: z.string(), line: ChannelLine, timezone: z.string() }),
  script_document_id: Uuid.nullable(),
  edit_plan_document_id: Uuid.nullable(),
  approvals: z.array(Approval),
  open_conflicts: z.number(),
  updated_at: Timestamp,
});
export type Project = z.infer<typeof Project>;

export const RevisionMeta = z.looseObject({
  id: Uuid,
  document_id: Uuid,
  number: z.number(),
  branch: z.enum(['main', 'proposal']),
  parent_revision_id: Uuid.nullable(),
  content_hash: z.string(),
  byte_size: z.number(),
  source: z.enum(['human', 'agent', 'process']),
  author_id: Uuid.nullable(),
  device_id: Uuid.nullable(),
  created_at: Timestamp,
});
export type RevisionMeta = z.infer<typeof RevisionMeta>;

export const RevisionWithContent = RevisionMeta.extend({ content: z.record(z.string(), z.unknown()) });

export const DocumentRecord = z.looseObject({
  id: Uuid,
  workspace_id: Uuid,
  project_id: Uuid.nullable(),
  kind: DocumentKind,
  title: z.string(),
  head_revision_id: Uuid.nullable(),
  deleted_at: z.string().nullable(),
});

export const OpenConflict = z.looseObject({
  id: Uuid,
  base_revision_id: Uuid.nullable(),
  head_revision_id: Uuid.nullable(),
  proposal_revision: RevisionMeta,
  created_at: Timestamp,
});

export const SaveResult = z.looseObject({
  status: z.enum(['saved', 'unchanged', 'conflict', 'proposal']),
  document_id: Uuid,
  replayed: z.boolean().optional(),
  revision: RevisionMeta.optional(),
  conflict_id: Uuid.optional(),
  head_revision: RevisionMeta.nullable().optional(),
  proposal_revision: RevisionMeta.optional(),
});
export type SaveResult = z.infer<typeof SaveResult>;

export const Comment = z.looseObject({
  id: Uuid,
  document_id: Uuid,
  revision_id: Uuid,
  block_id: z.string().nullable(),
  scene_id: z.string().nullable(),
  anchor: z.record(z.string(), z.unknown()),
  body: z.string(),
  author_kind: z.enum(['human', 'agent', 'process']),
  created_at: Timestamp,
  resolved_at: z.string().nullable(),
});
export type Comment = z.infer<typeof Comment>;

export const ChangeItem = z.object({
  seq: z.number(),
  entity: z.string(),
  entity_id: Uuid,
  action: z.enum(['insert', 'update', 'delete']),
  revision_id: Uuid.nullable(),
  created_at: Timestamp,
});

export const AssetVersion = z.looseObject({
  id: Uuid,
  version_label: z.string(),
  sha256: z.string().nullable(),
  sha256_status: Provenance,
  bytes: z.number().nullable(),
  bytes_status: Provenance,
  duration_value: z.number().nullable(),
  duration_unit: z.enum(['frames', 'samples']).nullable(),
  duration_status: Provenance,
  fps_num: z.number().nullable(),
  fps_den: z.number().nullable(),
  sample_rate: z.number().nullable(),
  loudness_lufs: z.number().nullable(),
  loudness_status: Provenance,
});

export const AssetLocationSummary = z.looseObject({
  location_id: Uuid,
  device_id: Uuid.nullable(),
  device_name: z.string().nullable(),
  root_id: Uuid.nullable(),
  relative_path: z.string().nullable(),
  object_id: Uuid.nullable(),
  availability: Availability,
  last_checked_at: z.string().nullable(),
  potentially_stale: z.boolean(),
});

export const AssetSummary = z.looseObject({
  id: Uuid,
  kind: AssetKind,
  title: z.string(),
  origin: AssetOrigin,
  description: z.string(),
  tags: z.array(z.string()),
  versions: z.array(AssetVersion),
  availability: z.array(AssetLocationSummary),
  rights_status: z.string().nullable(),
  open_incidents: z.number(),
});
export type AssetSummary = z.infer<typeof AssetSummary>;

export const Job = z.looseObject({
  id: Uuid,
  workspace_id: Uuid,
  project_id: Uuid.nullable(),
  type: JobType,
  status: JobStatus,
  generation: z.number(),
  required_capabilities: z.array(z.string()),
  lease_expires_at: z.string().nullable(),
  technical_failures: z.number(),
  is_simulated: z.boolean(),
  eligible_devices: z.number().optional(),
  attempts: z.array(z.record(z.string(), z.unknown())).optional(),
  artifacts: z.array(z.record(z.string(), z.unknown())).optional(),
});
export type Job = z.infer<typeof Job>;

export const Device = z.looseObject({
  id: Uuid,
  name: z.string(),
  platform: z.string(),
  status: z.enum(['pending', 'active', 'revoked']),
  capabilities: z.record(z.string(), z.string()),
  last_seen_at: z.string().nullable(),
  roots: z.array(z.looseObject({ id: Uuid, label: z.string() })).optional(),
});

export const MarketingVariant = z.looseObject({
  id: Uuid,
  family: MarketingFamily,
  text_content: z.string().nullable(),
  object_id: Uuid.nullable(),
  origin: z.enum(['manual', 'imported', 'generated']),
  request_id: Uuid.nullable(),
  replaces_variant_id: Uuid.nullable(),
  locked: z.boolean(),
  selected: z.boolean(),
  status: z.enum(['active', 'replaced', 'discarded']),
});
export type MarketingVariant = z.infer<typeof MarketingVariant>;

export const PublicationIntent = z.looseObject({
  id: Uuid,
  project_id: Uuid,
  connection_id: Uuid,
  status: PublicationStatus,
  timezone: z.string(),
  planned_at: z.string().nullable(),
  local_planned_at: z.string().nullable(),
  confirmation_kind: z.enum(['api', 'manual']).nullable(),
  external_id: z.string().nullable(),
  authorization_valid: z.boolean(),
  unresolved_attempt: z.boolean(),
});
export type PublicationIntent = z.infer<typeof PublicationIntent>;

export const AnyRecord = z.record(z.string(), z.unknown());
